/**
 * AiCommandCenter — AI 团队指挥中心
 *
 * Comprehensive dashboard for managing the AI employee team:
 * - System health (Brain daemon status)
 * - Goal/Pipeline/Task execution overview
 * - Skill registry with enable/disable toggles
 * - Active alerts with acknowledge/resolve actions
 * - Token budget & cost summary
 * - 24-hour log summary
 *
 * All data fetched from existing monitoring/devops/token-budget APIs.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Activity,
  Brain,
  Cpu,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  DollarSign,
  BarChart3,
  Shield,
  RefreshCw,
  ArrowRight,
  Loader2,
  Pause,
  Play,
  ListTodo,
  Target,
  GitBranch,
  Users,
  Bell,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ─────────────────────────────────────────

interface MonitoringOverview {
  system: { status: string; uptime: number; timestamp: string; version: string };
  goals: { total: number; active: number; completed: number; failed: number };
  pipelines: { total: number; running: number; completed: number; failed: number };
  steps: { total: number; completed: number; failed: number };
  approvals: { total: number; pending: number };
  autonomousTasks: { total: number; running: number; completed: number; failed: number };
}

interface SkillInfo {
  id: string;
  name: string;
  version: string;
  enabled: boolean;
  intents: string[];
  permissions: string[];
  retryPolicy: { max: number; delayMs?: number };
  escalation: 'human' | 'auto';
  status?: string;
}

interface Alert {
  id: string;
  ruleId: string;
  agentId: string;
  severity: 'info' | 'warning' | 'critical';
  status: string;
  message: string;
  metric: string;
  currentValue: string;
  threshold: string;
  firedAt: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
}

interface LogSummary {
  since: string;
  counts: {
    apiLogs: number;
    aiLogs: number;
    debugHistory: number;
    executionTraces: number;
    decisionTraces: number;
    slowQueries: number;
    skillExecutions: number;
  };
  ai: { totalCostUsd: number; totalPromptTokens: number; totalCompletionTokens: number; avgDurationMs: number };
  api: { avgDurationMs: number; maxDurationMs: number; p95DurationMs: number };
  skills: { successCount: number; failureCount: number; timeoutCount: number; avgDurationMs: number };
  topErrors: { error_code: string; count: number }[];
}

interface DevOpsDashboard {
  totalServers: number;
  onlineServers: number;
  offlineServers: number;
  degradedServers: number;
  maintenanceServers: number;
  activeAlerts: { info: number; warning: number; critical: number };
  servers: unknown[];
}

interface PendingTask {
  id: string;
  goal_id?: string | null;
  task_type: string | null;
  title: string | null;
  status: string;
  wait_reason: string | null;
  action_route: string | null;
  result_route: string | null;
  updated_at: string | null;
}

interface GoalTraceResponse {
  goal?: {
    id: string;
    intent?: string;
    status?: string;
    workflowGovernance?: {
      actionRecord?: {
        id?: string;
        moduleKey?: string;
        status?: string;
        riskLevel?: string;
        autonomyBand?: string;
      };
      approvalRequest?: {
        id?: string;
        queueKey?: string;
        status?: string;
      } | null;
    };
  };
  executionTraces: Array<{
    id?: string;
    step_id?: string;
    tool_name?: string;
    status?: string;
    duration_ms?: number;
    created_at?: string;
  }>;
  decisionTraces: Array<{
    id?: string;
    decision_type?: string;
    policy_name?: string;
    reasoning?: string | null;
    final_decision?: Record<string, unknown> | null;
    created_at?: string;
  }>;
  aiLogs: Array<{
    id?: string;
    provider?: string;
    model?: string;
    total_tokens?: number;
    cost_usd?: number | null;
    reasoning?: string | null;
    created_at?: string;
  }>;
}

// ─── Helpers ───────────────────────────────────────

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatCost(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

// ─── Component ─────────────────────────────────────

export default function AiCommandCenter() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState<MonitoringOverview | null>(null);
  const [skills, setSkills] = useState<{ total: number; skills: SkillInfo[] }>({ total: 0, skills: [] });
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [logSummary, setLogSummary] = useState<LogSummary | null>(null);
  const [devops, setDevops] = useState<DevOpsDashboard | null>(null);
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<PendingTask | null>(null);
  const [goalTrace, setGoalTrace] = useState<GoalTraceResponse | null>(null);

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    const results = await Promise.allSettled([
      api.get<MonitoringOverview>('/monitoring/overview'),
      api.get<{ total: number; skills: SkillInfo[] }>('/monitoring/skills'),
      api.get<Alert[]>('/devops/alerts?limit=10&severity=critical'),
      api.get<LogSummary>('/logs/summary'),
      api.get<DevOpsDashboard>('/devops/dashboard'),
      api.get<{ items: PendingTask[] }>(
        `/autonomous-tasks?limit=8${user?.id ? `&requestedByUserId=${encodeURIComponent(user.id)}` : ''}`,
      ),
    ]);

    if (results[0].status === 'fulfilled') setOverview(results[0].value);
    if (results[1].status === 'fulfilled') setSkills(results[1].value);
    if (results[2].status === 'fulfilled') setAlerts(results[2].value);
    if (results[3].status === 'fulfilled') setLogSummary(results[3].value);
    if (results[4].status === 'fulfilled') setDevops(results[4].value);
    if (results[5].status === 'fulfilled') {
      const items = results[5].value.items ?? [];
      setPendingTasks(items.filter((task) => !['completed', 'failed', 'cancelled'].includes(task.status)).slice(0, 5));
    }

    setLoading(false);
    setRefreshing(false);
  }, [user?.id]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  // Auto-refresh every 30s
  useEffect(() => {
    const timer = setInterval(() => void fetchAll(true), 30_000);
    return () => clearInterval(timer);
  }, [fetchAll]);

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await api.post(`/devops/alerts/${alertId}/acknowledge`, {});
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch { /* ignore */ }
  };

  const handleOpenTrace = async (task: PendingTask) => {
    if (!task.goal_id) return;
    setSelectedTask(task);
    setTraceOpen(true);
    setTraceLoading(true);
    setTraceError(null);
    setGoalTrace(null);
    try {
      const trace = await api.get<GoalTraceResponse>(`/monitoring/goals/${task.goal_id}/trace`);
      setGoalTrace(trace);
    } catch (error) {
      setTraceError(error instanceof Error ? error.message : 'Failed to load goal trace');
    } finally {
      setTraceLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sys = overview?.system;
  const goals = overview?.goals;
  const pipes = overview?.pipelines;
  const tasks = overview?.autonomousTasks;
  const approvals = overview?.approvals;

  return (
    <div className="space-y-6 p-6">
      {/* ─── Header ───────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            AI Team Command Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time monitoring, management and tracking of AI employee status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={sys?.status === 'ok' ? 'default' : 'destructive'} className="gap-1">
            {sys?.status === 'ok' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {sys?.status === 'ok' ? 'System Healthy' : 'Error'}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => void fetchAll(true)} disabled={refreshing}>
            <RefreshCw className={cn('h-4 w-4 mr-1', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ─── System Health Cards ──────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatusCard
          icon={<Activity className="h-5 w-5 text-green-500" />}
          label="Uptime"
          value={sys ? formatUptime(sys.uptime) : '--'}
          sub={sys ? `v${sys.version}` : ''}
        />
        <StatusCard
          icon={<Target className="h-5 w-5 text-primary" />}
          label="Goals"
          value={goals ? `${goals.active} active` : '--'}
          sub={goals ? `${goals.completed} done / ${goals.failed} failed` : ''}
        />
        <StatusCard
          icon={<GitBranch className="h-5 w-5 text-purple-500" />}
          label="Pipelines"
          value={pipes ? `${pipes.running} running` : '--'}
          sub={pipes ? `${pipes.completed} done / ${pipes.failed} failed` : ''}
        />
        <StatusCard
          icon={<Cpu className="h-5 w-5 text-orange-500" />}
          label="Autonomous Tasks"
          value={tasks ? `${tasks.running} running` : '--'}
          sub={tasks ? `total ${tasks.total}` : ''}
        />
      </div>

      {/* ─── Two-Column Layout ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2/3: Skills + Alerts */}
        <div className="lg:col-span-2 space-y-6">
          {/* ─── Skill Registry ─────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Skill Registry ({skills.total})
                </CardTitle>
                <Link to="/admin/skills">
                  <Button variant="ghost" size="sm" className="text-xs gap-1">
                    View All <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {skills.skills.slice(0, 8).map((skill) => (
                  <div
                    key={skill.id}
                    className="flex items-center justify-between rounded-lg border px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={cn(
                        'h-2 w-2 rounded-full shrink-0',
                        skill.enabled ? 'bg-green-500' : 'bg-gray-300',
                      )} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{skill.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          v{skill.version} · {skill.escalation === 'human' ? '🧑 Human' : '🤖 Auto'}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={skill.enabled ? 'default' : 'secondary'}
                      className="text-[10px] shrink-0"
                    >
                      {skill.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                ))}
              </div>
              {skills.total > 8 && (
                <p className="text-xs text-muted-foreground text-center mt-3">
                  +{skills.total - 8} more…
                </p>
              )}
            </CardContent>
          </Card>

          {/* ─── Active Alerts ──────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Active Alerts
                {devops && (devops.activeAlerts.critical + devops.activeAlerts.warning) > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {devops.activeAlerts.critical + devops.activeAlerts.warning}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                  All clear — no active alerts
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={cn(
                        'flex items-start justify-between rounded-lg border px-3 py-2',
                        alert.severity === 'critical' && 'border-destructive/50 bg-destructive/5',
                        alert.severity === 'warning' && 'border-yellow-500/50 bg-yellow-500/5',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{alert.message}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {alert.metric}: {alert.currentValue} (threshold: {alert.threshold})
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-xs"
                        onClick={() => void handleAcknowledgeAlert(alert.id)}
                      >
                        Ack
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ─── Approval Queue ─────────────────── */}
          {approvals && approvals.pending > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Pending Approval ({approvals.pending})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {approvals.pending} decisions awaiting human approval
                  </p>
                  <Link to="/e/approval-requests">
                    <Button variant="outline" size="sm" className="text-xs gap-1">
                      View All <ArrowRight className="h-3 w-3" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ListTodo className="h-5 w-5 text-primary" />
                  My Pending Tasks
                </CardTitle>
                <Link to="/admin/tasks">
                  <Button variant="ghost" size="sm" className="text-xs gap-1">
                    View All <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {pendingTasks.length === 0 ? (
                <div className="text-sm text-muted-foreground">No pending tasks right now.</div>
              ) : (
                <div className="space-y-2">
                  {pendingTasks.map((task) => {
                    const route = task.result_route || task.action_route || `/admin/tasks?taskId=${task.id}`;
                    return (
                      <div key={task.id} className="rounded-lg border px-3 py-2 transition-colors hover:bg-muted/40">
                        <div className="flex items-center justify-between gap-3">
                          <Link to={route} className="min-w-0 flex-1">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">{task.title ?? task.task_type ?? task.id}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {task.task_type ?? 'task'} · {task.wait_reason ?? task.status}
                              </p>
                            </div>
                          </Link>
                          <div className="flex shrink-0 items-center gap-2">
                            {task.goal_id ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-[10px]"
                                onClick={() => void handleOpenTrace(task)}
                              >
                                Trace
                              </Button>
                            ) : null}
                            <Badge variant="outline" className="shrink-0 text-[10px]">
                              {task.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right 1/3: Stats Sidebar */}
        <div className="space-y-6">
          {/* ─── Token / Cost Summary ─────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                24h AI Cost
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {logSummary ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Cost</span>
                    <span className="font-mono font-medium">{formatCost(logSummary.ai.totalCostUsd)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Prompt Tokens</span>
                    <span className="font-mono">{formatNumber(logSummary.ai.totalPromptTokens)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Completion Tokens</span>
                    <span className="font-mono">{formatNumber(logSummary.ai.totalCompletionTokens)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Avg Latency</span>
                    <span className="font-mono">{logSummary.ai.avgDurationMs.toFixed(0)}ms</span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
            </CardContent>
          </Card>

          {/* ─── API Performance ──────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                24h API Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {logSummary ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Requests</span>
                    <span className="font-mono font-medium">{formatNumber(logSummary.counts.apiLogs)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">P95 Latency</span>
                    <span className="font-mono">{logSummary.api.p95DurationMs.toFixed(0)}ms</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Max Latency</span>
                    <span className="font-mono">{logSummary.api.maxDurationMs.toFixed(0)}ms</span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
            </CardContent>
          </Card>

          {/* ─── Skill Execution Stats ───────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                24h Skill Execution
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {logSummary ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Success</span>
                    <span className="font-mono text-green-600">{logSummary.skills.successCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Failures</span>
                    <span className="font-mono text-red-600">{logSummary.skills.failureCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Timeouts</span>
                    <span className="font-mono text-yellow-600">{logSummary.skills.timeoutCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Success Rate</span>
                    <span className="font-mono font-medium">
                      {logSummary.skills.successCount + logSummary.skills.failureCount > 0
                        ? `${((logSummary.skills.successCount / (logSummary.skills.successCount + logSummary.skills.failureCount)) * 100).toFixed(1)}%`
                        : 'N/A'}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
            </CardContent>
          </Card>

          {/* ─── Log Counts ──────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  24h Log Stats
                </CardTitle>
                <Link to="/admin/logs">
                  <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1">
                    View <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {logSummary ? (
                <>
                  <LogCountRow label="API Requests" count={logSummary.counts.apiLogs} />
                  <LogCountRow label="AI Calls" count={logSummary.counts.aiLogs} />
                  <LogCountRow label="Exec Traces" count={logSummary.counts.executionTraces} />
                  <LogCountRow label="Decision Traces" count={logSummary.counts.decisionTraces} />
                  <LogCountRow label="Debug History" count={logSummary.counts.debugHistory} />
                  <LogCountRow label="Slow Queries" count={logSummary.counts.slowQueries} />
                  <LogCountRow label="Skill Executions" count={logSummary.counts.skillExecutions} />
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
            </CardContent>
          </Card>

          {/* ─── Top Errors ──────────────────── */}
          {logSummary && logSummary.topErrors.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Top-5 Errors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {logSummary.topErrors.map((err) => (
                  <div key={err.error_code} className="flex justify-between text-sm">
                    <span className="text-muted-foreground truncate max-w-[160px]">{err.error_code}</span>
                    <Badge variant="destructive" className="text-[10px]">{err.count}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ─── DevOps Agent Summary ────────── */}
          {devops && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4 text-purple-500" />
                  Agent Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Online</span>
                  <span className="font-mono text-green-600">{devops.onlineServers}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Degraded</span>
                  <span className="font-mono text-yellow-600">{devops.degradedServers}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Offline</span>
                  <span className="font-mono text-red-600">{devops.offlineServers}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ─── Quick Nav Links ──────────────────── */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-2">
            <QuickLink to="/admin/skills" icon={<Zap className="h-3.5 w-3.5" />} label="Skill Manager" />
            <QuickLink to="/admin/logs" icon={<BarChart3 className="h-3.5 w-3.5" />} label="Log Explorer" />
            <QuickLink to="/admin/tasks" icon={<ListTodo className="h-3.5 w-3.5" />} label="Task Center" />
            <QuickLink to="/e/goals" icon={<Target className="h-3.5 w-3.5" />} label="Goals" />
            <QuickLink to="/e/pipelines" icon={<GitBranch className="h-3.5 w-3.5" />} label="Pipelines" />
            <QuickLink to="/e/autonomous-tasks" icon={<Cpu className="h-3.5 w-3.5" />} label="Autonomous Tasks" />
            <QuickLink to="/e/approval-requests" icon={<Shield className="h-3.5 w-3.5" />} label="Approval Queue" />
            <QuickLink to="/e/activity-log" icon={<Clock className="h-3.5 w-3.5" />} label="Audit Logs" />
            <QuickLink to="/e/conversations" icon={<Bell className="h-3.5 w-3.5" />} label="Conversations" />
          </div>
        </CardContent>
      </Card>

      <Dialog open={traceOpen} onOpenChange={setTraceOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Goal Trace
              {selectedTask ? ` · ${selectedTask.title ?? selectedTask.task_type ?? selectedTask.id}` : ''}
            </DialogTitle>
          </DialogHeader>

          {traceLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading trace…
            </div>
          ) : traceError ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {traceError}
            </div>
          ) : goalTrace ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <TraceStat label="Goal Status" value={goalTrace.goal?.status ?? '--'} />
                <TraceStat label="Intent" value={goalTrace.goal?.intent ?? '--'} />
                <TraceStat label="Execution Traces" value={String(goalTrace.executionTraces.length)} />
                <TraceStat label="Decision Traces" value={String(goalTrace.decisionTraces.length)} />
              </div>

              {goalTrace.goal?.workflowGovernance?.actionRecord ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Governed Execution</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-3 text-sm">
                    <TraceStat label="Module" value={goalTrace.goal.workflowGovernance.actionRecord.moduleKey ?? '--'} />
                    <TraceStat label="Action Status" value={goalTrace.goal.workflowGovernance.actionRecord.status ?? '--'} />
                    <TraceStat label="Risk Level" value={goalTrace.goal.workflowGovernance.actionRecord.riskLevel ?? '--'} />
                    <TraceStat label="Autonomy" value={goalTrace.goal.workflowGovernance.actionRecord.autonomyBand ?? '--'} />
                    <TraceStat label="Approval Queue" value={goalTrace.goal.workflowGovernance.approvalRequest?.queueKey ?? '--'} />
                    <TraceStat label="Approval Status" value={goalTrace.goal.workflowGovernance.approvalRequest?.status ?? 'n/a'} />
                  </CardContent>
                </Card>
              ) : (
                <div className="rounded-lg border p-4 text-sm text-muted-foreground">
                  No governance payload found for this goal yet.
                </div>
              )}

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Execution Timeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {goalTrace.executionTraces.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No execution traces yet.</div>
                  ) : (
                    goalTrace.executionTraces.slice(0, 8).map((trace, index) => (
                      <TimelineRow
                        key={trace.id ?? `${trace.step_id ?? 'execution'}-${index}`}
                        title={trace.step_id ?? trace.tool_name ?? 'execution'}
                        subtitle={trace.tool_name ?? 'workflow step'}
                        meta={[
                          trace.status ?? '--',
                          typeof trace.duration_ms === 'number' ? `${trace.duration_ms}ms` : null,
                          trace.created_at ?? null,
                        ].filter(Boolean).join(' · ')}
                      />
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Decision Timeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {goalTrace.decisionTraces.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No decision traces yet.</div>
                  ) : (
                    goalTrace.decisionTraces.slice(0, 8).map((trace, index) => {
                      const finalDecisionPreview = trace.final_decision
                        ? JSON.stringify(trace.final_decision).slice(0, 180)
                        : null;
                      return (
                        <TimelineRow
                          key={trace.id ?? `${trace.decision_type ?? 'decision'}-${index}`}
                          title={trace.decision_type ?? 'decision'}
                          subtitle={trace.policy_name ?? 'policy'}
                          meta={trace.created_at ?? '--'}
                          detail={trace.reasoning ?? finalDecisionPreview ?? undefined}
                        />
                      );
                    })
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">AI Logs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {goalTrace.aiLogs.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No AI logs linked to this goal.</div>
                  ) : (
                    goalTrace.aiLogs.slice(0, 6).map((log, index) => (
                      <TimelineRow
                        key={log.id ?? `${log.model ?? 'ai'}-${index}`}
                        title={log.model ?? 'model'}
                        subtitle={log.provider ?? 'provider'}
                        meta={[
                          typeof log.total_tokens === 'number' ? `${log.total_tokens} tokens` : null,
                          typeof log.cost_usd === 'number' ? `$${log.cost_usd.toFixed(4)}` : null,
                          log.created_at ?? null,
                        ].filter(Boolean).join(' · ')}
                        detail={log.reasoning ?? undefined}
                      />
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No trace data loaded.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────

function StatusCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-lg font-bold">{value}</p>
            {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LogCountRow({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{formatNumber(count)}</span>
    </div>
  );
}

function QuickLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link to={to}>
      <Button variant="outline" size="sm" className="text-xs gap-1.5 h-8">
        {icon}
        {label}
      </Button>
    </Link>
  );
}

function TraceStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-medium">{value}</p>
    </div>
  );
}

function TimelineRow({
  title,
  subtitle,
  meta,
  detail,
}: {
  title: string;
  subtitle: string;
  meta: string;
  detail?: string;
}) {
  return (
    <div className="rounded-lg border px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
        </div>
        <p className="shrink-0 text-[10px] text-muted-foreground">{meta}</p>
      </div>
      {detail ? (
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{detail}</p>
      ) : null}
    </div>
  );
}
